import { BundleItem, ProductImage, RootData } from 'src/types/graphql/product';
import { PRODUCT_TYPES } from 'src/types/subscription';
import moment from 'moment';
export interface InkPlan {
  inkSKU: string;
  pagesPerMonth: number;
  leasePrice: number;
  paperPrice: number;
  planPrice: number;
  totalPrice: number;
  recommendedPlan: boolean;
  commitmentPeriod: number;
}

export interface PrinterOfferPlan {
  fullspecification: string;
  specificationList: string;
  thumbnailImage: string;
  shortDescription: string;
  shortConfigName: string;
  mediaGallery: ProductImage[];
  stockStatus: string;
}
export interface LeasePlan {
  price: number;
}

export interface PrinterItem {
  printerName: string;
  recommendedPrinter: boolean;
  currentPrinter: boolean;
  productSKU: string;
  modelPurpose: string;
  stockStatus: string;
  isPaperActive: boolean;
  mediaGallery: ProductImage[];
  inkPlan: InkPlan[];
  fullspecification: string;
  specificationList: string;
  thumbnailImage: string;
  shortDescription: {
    html: string;
  };
}
export interface PrinterSpecs {
  adfCapacity: number;
  autoDocFeeder: boolean;
  builtInFax: boolean;
  dimensions: {
    width: number;
    depth: number;
    height: number;
    unit: string;
  };
  ethernetNetworking: boolean;
  inkType: string;
  lcd: boolean;
  memoryCard: boolean;
  paperInput: number;
  photoTray: boolean;
  printCopyScan: boolean;
  printSpeed: number;
  wirelessCapability: boolean;
}

export function buildPrinterList(
  data: RootData,
  skuList: string[],
  activePaperSubscription: boolean,
  optionsData: any,
  inkProductSku: string
): { currentPrinterList: PrinterItem[]; nonCurrentPrinters: PrinterItem[] } {
  const products = data?.products?.items ?? [];
  const optionDetails = optionsData?.entities?.[0]?.edit;
  const filteredEntities = optionDetails?.filter(
    (entity: any) => entity.product?.value?.productSku === inkProductSku
  );

  const printerList: PrinterItem[] = skuList.reduce(
    (acc: PrinterItem[], sku, index) => {
      const foundProduct = products.find((product) => product.sku === sku);
      if (foundProduct) {
        const currentPrinter = index === 0; //Only first item from skuList is current printer
        let printerOfferPlans: PrinterOfferPlan[] = [];
        let printerName = '';

        const offerPlansResult = getCoreOfferPlans(foundProduct?.items);
        printerOfferPlans = offerPlansResult?.printerOfferPlans || [];
        printerName =
          printerOfferPlans.length > 0
            ? printerOfferPlans[0].shortConfigName
            : '';
        //For now this is hardcoded for recommended printer[Smart Tank]
        const recommendedPrinter = printerName === 'HP Smart Tank';
        acc.push({
          printerName,
          recommendedPrinter,
          currentPrinter,
          modelPurpose: foundProduct.hp_model_purpose,
          productSKU: foundProduct.sku,
          stockStatus:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].stockStatus
              : '',
          isPaperActive: activePaperSubscription,
          mediaGallery:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].mediaGallery
              : [],
          inkPlan: !currentPrinter
            ? getInkPlans(foundProduct?.items, activePaperSubscription).inkPlans
            : [
                getCurrentPrinterInkPlan(
                  filteredEntities,
                  activePaperSubscription,
                  currentPrinter
                ) as InkPlan
              ],
          fullspecification:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].fullspecification
              : '',
          specificationList:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].specificationList
              : '',
          thumbnailImage:
            !currentPrinter && printerOfferPlans.length > 0
              ? printerOfferPlans[0].thumbnailImage
              : '',
          shortDescription: {
            html:
              !currentPrinter && printerOfferPlans.length > 0
                ? printerOfferPlans[0].shortDescription || ''
                : ''
          }
        });
      }
      return acc;
    },
    []
  );

  // ✅ Split into two lists
  const currentPrinterList = printerList.filter((p) => p.currentPrinter);
  const nonCurrentPrinters = printerList.filter((p) => !p.currentPrinter);

  return { currentPrinterList, nonCurrentPrinters };
}

function getInkPlans(
  bundleItems: BundleItem[],
  activePaperSubscription: boolean
): { inkPlans: InkPlan[] } {
  // Extract lease plans
  const leasePlans: LeasePlan[] = bundleItems.flatMap((item) =>
    item.options
      .filter(
        (option) =>
          option.product.hp_product_type_label ===
          PRODUCT_TYPES.LEASE_RECEIVABLE
      )
      .map((option) => ({
        price: option.product.price?.regularPrice?.amount?.value ?? 0
      }))
  );

  // Extract paper plans mapped by pagesPerMonth
  const paperPlansMap: Record<number, number> = bundleItems
    .flatMap((item) =>
      item.options
        .filter(
          (option) =>
            activePaperSubscription &&
            option.product.hp_product_type_label === PRODUCT_TYPES.INSTANT_PAPER
        )
        .map((option) => ({
          pages: option.product.hp_pages_per_month ?? 0,
          price: option.product.price?.regularPrice?.amount?.value ?? 0
        }))
    )
    .reduce(
      (map, { pages, price }) => {
        map[pages] = price;
        return map;
      },
      {} as Record<number, number>
    );

  // ✅ Instant Ink Plans
  const inkPlans: InkPlan[] = bundleItems.flatMap((item) =>
    item.options
      .filter(
        (option) =>
          option.product.hp_product_type_label === PRODUCT_TYPES.INSTANT_INK
      )
      .map((option, index) => {
        const product = option.product;
        const pages = product.hp_pages_per_month ?? 0;
        const paperPrice = paperPlansMap[pages] ?? 0;
        const months = product.hp_contract_duration ?? 0;
        const duration = moment.duration(months, 'months');
        const upgradeEligibility = duration.years();

        return {
          inkSKU: product.sku,
          pagesPerMonth: pages,
          commitmentPeriod: upgradeEligibility,
          leasePrice: leasePlans.length > 0 ? leasePlans[0].price : 0,
          paperPrice,
          planPrice: product.price?.regularPrice?.amount?.value ?? 0,
          totalPrice:
            (leasePlans.length > 0 ? leasePlans[0].price : 0) +
            paperPrice +
            (product.price?.regularPrice?.amount?.value ?? 0),
          recommendedPlan: index === 0 // First plan is recommended
        };
      })
  );

  return { inkPlans };
}

function getCoreOfferPlans(bundleItems: BundleItem[]): {
  printerOfferPlans: PrinterOfferPlan[];
} {
  const printerOfferPlans: PrinterOfferPlan[] = bundleItems.flatMap((item) => {
    const printerOptions = item.options.filter(
      (option) => option.product.hp_product_type_label === PRODUCT_TYPES.PRINTER
    );

    if (printerOptions.length > 0) {
      const product = printerOptions[0].product; // ✅ Take only the first option
      return [
        {
          printerSKU: product.sku,
          fullspecification: product.hp_product_full_specification ?? '',
          shortConfigName: product.hp_short_config_name ?? '',
          specificationList: product.hp_specifications_json,
          thumbnailImage: product.thumbnail.url,
          shortDescription: product.short_description?.html ?? '',
          mediaGallery: product.media_gallery ?? [],
          stockStatus: product.stock_status
        }
      ];
    }

    return []; // If no printer option exists
  });

  return { printerOfferPlans };
}

function getCurrentPrinterInkPlan(
  filteredEntities: any[],
  activePaperSubscription: boolean,
  currentPrinter: boolean
): InkPlan | null {
  if (!currentPrinter) return null;

  const pagesPerMonth =
    filteredEntities?.[0]?.product?.value?.pagesPerMonth || 0;

  const leasePrice =
    filteredEntities?.[0]?.outcomes.length > 0
      ? filteredEntities?.[0]?.outcomes?.filter(
          (outcome: any) =>
            outcome.type === PRODUCT_TYPES.LEASE_PRICE_TYPE &&
            outcome.value?.productType === PRODUCT_TYPES.LEASE_RECEIVABLE
        )?.[0]?.value?.price?.cost || 0
      : 0;

  const paperPrice =
    activePaperSubscription && filteredEntities?.[0]?.outcomes.length > 0
      ? filteredEntities?.[0]?.outcomes?.filter(
          (outcome: any) =>
            outcome.type === PRODUCT_TYPES.LEASE_PRICE_TYPE &&
            outcome.value?.productType === PRODUCT_TYPES.INSTANT_PAPER
        )?.[0]?.value?.price?.cost || 0
      : 0;

  const planPrice = filteredEntities?.[0]?.product?.value?.price?.cost || 0;

  const totalPrice = planPrice + leasePrice + paperPrice;

  return {
    commitmentPeriod: 0,
    inkSKU: filteredEntities?.[0]?.product?.value?.productSku || '',
    recommendedPlan: false,
    pagesPerMonth,
    leasePrice,
    paperPrice,
    planPrice,
    totalPrice
  };
}
